// src/hooks/useScanLifecycle.ts - React hook for scan lifecycle management

import { useState, useCallback } from 'react';
import { scanLifecycleService } from '../services/scanLifecycle.service';
import type { 
  Scan,
  ScanVersion,
  ScanMetadata,
  CreateScanDto,
  CreateScanVersionDto,
  ScanStatus,
  ScanCreationResponse
} from '../types/scan.types';
import type { Scene } from '../types/core.types';

interface ScanLifecycleState {
  currentScan: Scan | null;
  scanMetadata: ScanMetadata | null;
  scanVersions: ScanVersion[];
  activeVersion: ScanVersion | null;
  status: ScanStatus | null;
  isLoading: boolean;
  error: string | null;
}

interface UseScanLifecycleReturn {
  // State
  state: ScanLifecycleState;
  
  // Basic operations
  createScanWithStatus: (data: CreateScanDto, status: ScanStatus) => Promise<ScanCreationResponse>;
  updateScanStatus: (scanId: string, status: ScanStatus, subStatus?: string) => Promise<Scan>;
  
  // Lifecycle transitions
  transitionToUploaded: (scanId: string, s3FolderPath: string, inputImageCount: number, sequenceUuid: string) => Promise<Scan>;
  transitionToProcessing: (scanId: string) => Promise<Scan>;
  transitionToCompleted: (scanId: string, reconstructionFolder?: string) => Promise<Scan>;
  transitionToFailed: (scanId: string, errorMessage?: string) => Promise<Scan>;
  
  // Metadata operations
  createScanMetadata: (scanId: string, metadata: Record<string, any>) => Promise<ScanMetadata>;
  updateScanMetadata: (scanId: string, metadata: Record<string, any>) => Promise<ScanMetadata>;
  
  // Version operations
  createScanVersion: (scanId: string, versionData: Omit<CreateScanVersionDto, 'scanId'>) => Promise<ScanVersion>;
  getScanVersions: (scanId: string) => Promise<ScanVersion[]>;
  getScanVersion: (versionId: string) => Promise<ScanVersion>;
  setActiveScanVersion: (scanId: string, versionId: string) => Promise<Scan>;
  createNewVersionWithData: (scanId: string, versionData: Omit<CreateScanVersionDto, 'scanId' | 'isFromMobileApp'>, setAsActive?: boolean) => Promise<{ version: ScanVersion; scan?: Scan }>;
  
  // High-level workflows
  createCompleteWebScan: (title: string, scene: Scene, sequenceUuid: string, additionalMetadata?: Record<string, any>) => Promise<{ scan: Scan; metadata: ScanMetadata }>;
  completeUploadWorkflow: (scanId: string, s3FolderPath: string, fileCount: number, sequenceUuid: string, additionalMetadata?: Record<string, any>) => Promise<Scan>;
  completeReconstructionWorkflow: (scanId: string, sequenceUUID: string, images: Array<{
    image_key: string;
    timestamp: string;
    image_width_px: number;
    image_height_px: number;
    focal_length_35mm: number;
  }>) => Promise<void>;
  
  // Utility functions
  clearState: () => void;
  resetError: () => void;
}

export function useScanLifecycle(): UseScanLifecycleReturn {
  const [state, setState] = useState<ScanLifecycleState>({
    currentScan: null,
    scanMetadata: null,
    scanVersions: [],
    activeVersion: null,
    status: null,
    isLoading: false,
    error: null
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  const setScan = useCallback((scan: Scan) => {
    setState(prev => ({ 
      ...prev, 
      currentScan: scan, 
      status: scan.status,
      isLoading: false,
      error: null 
    }));
  }, []);

  const setMetadata = useCallback((metadata: ScanMetadata) => {
    setState(prev => ({ ...prev, scanMetadata: metadata }));
  }, []);

  const setVersions = useCallback((versions: ScanVersion[]) => {
    setState(prev => ({ ...prev, scanVersions: versions }));
  }, []);

  const setActiveVersion = useCallback((version: ScanVersion) => {
    setState(prev => ({ ...prev, activeVersion: version }));
  }, []);

  // Basic operations
  const createScanWithStatus = useCallback(async (data: CreateScanDto, status: ScanStatus): Promise<ScanCreationResponse> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.createScanWithStatus(data, status);
      setScan(result.scan);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create scan';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  const updateScanStatus = useCallback(async (scanId: string, status: ScanStatus, subStatus?: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.updateScanStatus(scanId, status, subStatus);
      setScan(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update scan status';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  // Lifecycle transitions
  const transitionToUploaded = useCallback(async (scanId: string, s3FolderPath: string, inputImageCount: number, sequenceUuid: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.transitionToUploaded(scanId, s3FolderPath, inputImageCount, sequenceUuid);
      setScan(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to transition to uploaded';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  const transitionToProcessing = useCallback(async (scanId: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.transitionToProcessing(scanId);
      setScan(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to transition to processing';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  const transitionToCompleted = useCallback(async (scanId: string, reconstructionFolder?: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.transitionToCompleted(scanId, reconstructionFolder);
      setScan(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to transition to completed';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  const transitionToFailed = useCallback(async (scanId: string, errorMessage?: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.transitionToFailed(scanId, errorMessage);
      setScan(result);
      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to transition to failed';
      setError(errorMsg);
      throw error;
    }
  }, [setLoading, setScan, setError]);

  // Metadata operations
  const createScanMetadata = useCallback(async (scanId: string, metadata: Record<string, any>): Promise<ScanMetadata> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.createScanMetadata(scanId, metadata);
      setMetadata(result);
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create scan metadata';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setMetadata, setError]);

  const updateScanMetadata = useCallback(async (scanId: string, metadata: Record<string, any>): Promise<ScanMetadata> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.updateScanMetadata(scanId, metadata);
      setMetadata(result);
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update scan metadata';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setMetadata, setError]);

  // Version operations
  const createScanVersion = useCallback(async (scanId: string, versionData: Omit<CreateScanVersionDto, 'scanId'>): Promise<ScanVersion> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.createScanVersion(scanId, versionData);
      
      // Refresh versions list if we're tracking the same scan
      if (state.currentScan?.scanId === scanId) {
        try {
          const versions = await scanLifecycleService.getScanVersions(scanId);
          setVersions(versions);
        } catch (versionError) {
          console.warn('Failed to refresh versions after creation:', versionError);
        }
      }
      
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create scan version';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setVersions, setError, state.currentScan?.scanId]);

  const getScanVersions = useCallback(async (scanId: string): Promise<ScanVersion[]> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.getScanVersions(scanId);
      setVersions(result);
      
      // Set active version if we have the current scan
      if (state.currentScan?.scanId === scanId && state.currentScan.activeVersionId) {
        const activeVer = result.find(v => v.versionId === state.currentScan!.activeVersionId);
        if (activeVer) {
          setActiveVersion(activeVer);
        }
      }
      
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get scan versions';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setVersions, setActiveVersion, setError, state.currentScan]);

  const getScanVersion = useCallback(async (versionId: string): Promise<ScanVersion> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.getScanVersion(versionId);
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get scan version';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setError]);

  const setActiveScanVersion = useCallback(async (scanId: string, versionId: string): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.setActiveScanVersion(scanId, versionId);
      setScan(result);
      
      // Update active version in state
      const activeVer = state.scanVersions.find(v => v.versionId === versionId);
      if (activeVer) {
        setActiveVersion(activeVer);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set active scan version';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setActiveVersion, setError, state.scanVersions]);

  const createNewVersionWithData = useCallback(async (
    scanId: string,
    versionData: Omit<CreateScanVersionDto, 'scanId' | 'isFromMobileApp'>,
    setAsActive: boolean = false
  ): Promise<{ version: ScanVersion; scan?: Scan }> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.createNewVersionWithData(scanId, versionData, setAsActive);
      
      if (result.scan) {
        setScan(result.scan);
      }
      
      // Refresh versions list
      if (state.currentScan?.scanId === scanId) {
        try {
          const versions = await scanLifecycleService.getScanVersions(scanId);
          setVersions(versions);
          
          if (setAsActive) {
            setActiveVersion(result.version);
          }
        } catch (versionError) {
          console.warn('Failed to refresh versions after creation:', versionError);
        }
      }
      
      setLoading(false);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create new version with data';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setVersions, setActiveVersion, setError, state.currentScan?.scanId]);

  // High-level workflows
  const createCompleteWebScan = useCallback(async (
    title: string, 
    scene: Scene, 
    sequenceUuid: string,
    additionalMetadata: Record<string, any> = {}
  ): Promise<{ scan: Scan; metadata: ScanMetadata }> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.createCompleteWebScan(title, scene, sequenceUuid, additionalMetadata);
      setScan(result.scan);
      setMetadata(result.metadata);
      setLoading(false);
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create complete web scan';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setMetadata, setError]);

  const completeUploadWorkflow = useCallback(async (
    scanId: string,
    s3FolderPath: string,
    fileCount: number,
    sequenceUuid: string,
    additionalMetadata?: Record<string, any>
  ): Promise<Scan> => {
    try {
      setLoading(true);
      const result = await scanLifecycleService.completeUploadWorkflow(scanId, s3FolderPath, fileCount, sequenceUuid, additionalMetadata);
      setScan(result);
      
      // Refresh versions to get updated active version
      try {
        const versions = await scanLifecycleService.getScanVersions(scanId);
        setVersions(versions);
        
        const activeVer = versions.find(v => v.versionId === result.activeVersionId);
        if (activeVer) {
          setActiveVersion(activeVer);
        }
      } catch (versionError) {
        console.warn('Failed to refresh versions after upload:', versionError);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete upload workflow';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setScan, setVersions, setActiveVersion, setError]);

  const completeReconstructionWorkflow = useCallback(async (
    scanId: string,
    sequenceUUID: string,
    images: Array<{
      image_key: string;
      timestamp: string;
      image_width_px: number;
      image_height_px: number;
      focal_length_35mm: number;
    }>
  ): Promise<void> => {
    try {
      setLoading(true);
      await scanLifecycleService.completeReconstructionWorkflow(scanId, sequenceUUID, images);
      setLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete reconstruction workflow';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setError]);

  // Utility functions
  const clearState = useCallback(() => {
    setState({
      currentScan: null,
      scanMetadata: null,
      scanVersions: [],
      activeVersion: null,
      status: null,
      isLoading: false,
      error: null
    });
  }, []);

  const resetError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    state,
    createScanWithStatus,
    updateScanStatus,
    transitionToUploaded,
    transitionToProcessing,
    transitionToCompleted,
    transitionToFailed,
    createScanMetadata,
    updateScanMetadata,
    createScanVersion,
    getScanVersions,
    getScanVersion,
    setActiveScanVersion,
    createNewVersionWithData,
    createCompleteWebScan,
    completeUploadWorkflow,
    completeReconstructionWorkflow,
    clearState,
    resetError
  };
} 